import type { FC, MouseEventHandler } from "react";
import { type FormEventHandler, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type DeliveryHistories,
  type DeliveryHistory,
  type DeliveryHistorySearchInput,
  useDeliveryHistoriesRestApi,
} from "../../../hooks/useDeliveryHistoriesRestApi";
import {
  type DeliveryOpenRateAnalyticsResponse,
  getOpenRateAnalytics,
} from "../../../utils/open-rate-utils";
import DeliveryHistoriesPage from "./DeliveryHistoriesPage";

const DeliveryHistoriesPageContainer: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState<DeliveryHistorySearchInput>();
  const { error, data, refetch } = useDeliveryHistoriesRestApi({
    onCompleted: async () => {
      setIsLoading(false);
    },
  });

  const [deliveryHistories, setDeliveryHistories] = useState<DeliveryHistories>(
    {
      totalCount: 0,
      nodes: [],
    },
  );

  const [openRates, setOpenRates] = useState<DeliveryOpenRateAnalyticsResponse>(
    {},
  );
  const [openRatesLoaded, setOpenRatesLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (isMounted && data) {
      setDeliveryHistories(data.deliveryHistories);
    }
    return () => {
      isMounted = false;
    };
  }, [data]);

  useEffect(() => {
    if (deliveryHistories.nodes.length === 0) {
      // レコードがないときは取得状態をリセット
      setOpenRates({});
      setOpenRatesLoaded(false);
      return;
    }

    const deliveryIds = deliveryHistories.nodes
      .map(history => history?.deliveryId)
      .filter(Boolean);

    setOpenRatesLoaded(false);
    let isMounted = true;

    const fetchOpenRates = async () => {
      try {
        const openRates = await getOpenRateAnalytics(deliveryIds);
        if (isMounted) {
          setOpenRates(openRates);
        }
      } catch (_error) {
        // エラーを無視
      } finally {
        if (isMounted) {
          setOpenRatesLoaded(true);
        }
      }
    };
    fetchOpenRates();

    return () => {
      isMounted = false;
    };
  }, [deliveryHistories]);

  const onResend = useCallback(
    (selectedHistory: DeliveryHistory) => {
      const {
        from,
        subject,
        content,
        deliveryType: category,
        hrHash,
      } = selectedHistory;
      navigate("/emails/bulk/confirm", {
        state: {
          subject,
          content,
          from,
          category,
          hrHash,
        },
      });
    },
    [navigate],
  );

  const onSelectHistory = useCallback(
    (id: string) => {
      const deliveryHistory: DeliveryHistory = deliveryHistories.nodes.find(
        (deliveryHistory: DeliveryHistory) => deliveryHistory.id === id,
      )!;
      navigate("/email/new", {
        state: { deliveryHistory },
      });
    },
    [navigate, deliveryHistories],
  );

  const onChangeSearchInput = useCallback(
    (newSearchInput: DeliveryHistorySearchInput) => {
      setSearchInput(newSearchInput);
    },
    [],
  );

  const onSearch: FormEventHandler = useCallback(
    async event => {
      event.preventDefault();
      setIsLoading(true);
      await refetch({
        filter: searchInput,
        offset: 0,
        limit: 50,
      });
    },
    [searchInput, refetch],
  );

  const onClearFilter: MouseEventHandler = useCallback(
    async event => {
      event.preventDefault();
      setSearchInput({
        ...searchInput,
      });
    },
    [searchInput],
  );

  if (error) {
    return <div>{JSON.stringify(error, null, 2)}</div>;
  }
  return (
    <DeliveryHistoriesPage
      isLoading={isLoading}
      searchInput={searchInput!}
      deliveryHistories={deliveryHistories}
      openRates={openRates}
      openRatesLoaded={openRatesLoaded}
      onSelectHistory={onSelectHistory}
      onChangeSearchInput={onChangeSearchInput}
      onSearch={onSearch}
      onClearFilter={onClearFilter}
      onResend={onResend}
    />
  );
};

export default DeliveryHistoriesPageContainer;
